import axios from "axios";
class QuestionnaireService {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  /**
* @author Edgar Escobedo
* @description Create a new questionnaire 
* @date 18-08-2022
* @param {String} token
* @param {String} companyId
* @param {String} clientId
* @param {String} templateId
* @param {String} name
* @param {String} description
* @param {String} logo
* @returns {Object}
* @memberof QuestionnaireService
*/
  async createQuestionnaire(token, companyId, clientId, templateId, name, description, logo) {
    let response = await axios.post(
      `${this.apiUrl}/questionnaire`,
      { companyId, clientId, templateId, name, description, logo },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    return response.data;
  }


  /**
 * @author Edgar Escobedo
 * @description Get all templates
 * @date 18-08-2028
 * @param {String} token
 * @returns {Object}
 * @memberof QuestionnaireService
 */

  async getAllTemplatesByCompaniIdAndClienId(clientId, companyId, token) {
    const url = new URL(`${this.apiUrl}/templates`);
    if (clientId && clientId !== "") url.searchParams.append('clientId', clientId);
    if (companyId && companyId !== "") url.searchParams.append('companyId', companyId);

    let response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response
  }

  /**
  * @author Edgar Escobedo
  * @description update questionnaire
  * @date 22-08-2028
  * @param {String} token
  * @param {String} id
  * @param {String} name 
  * @param {String} description
  * @param {String} logo 
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async updateQuestionnaireById(id, name, description, templateId, logo, token) {
    let response = await axios.put(
      `${this.apiUrl}/update-questionnaire`,
      { id, name, description, templateId, logo },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description get questions by questionnaire
  * @date 24-08-2028
  * @param {String} token
  * @param {String} questionnaireId
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async getQuestionsbyQuestionnaireId(questionnaireId, token) {
    let response = await axios.get(`${this.apiUrl}/questions-for-questionnaire?questionnaireId=${questionnaireId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  /**
* @author Edgar Escobedo
* @description delete questions by questionnaireId
* @date 26-08-2028
* @param {String} token
* @param {String} questionnaireId
* @returns {Object}
* @memberof QuestionnaireService
*/

  async deleteQuestionsforQuestionnaireByQuestionaireId(questionnaireId, token) {
    let response = await axios.delete(
      `${this.apiUrl}/questions-by-questionnaireId?questionnaireId=${questionnaireId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description update question by id
  * @date 29-08-2028
  * @param {String} token
  * @param {String} questionId
  * @param {String} fieldType
  * @param {String} question
  * @param {String} helpText
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async updateQuestionById(questionId, fieldType, question, helpText, token) {
    let response = await axios.put(
      `${this.apiUrl}/update-a-question`,
      { questionId, fieldType, question, helpText },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description get documents by client
  * @date 02-09-2022
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async getDocumentsForClient(token) {
    let response = await axios.get(`${this.apiUrl}/clientDocument`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }

  /**
   * @author Edgar Escobedo
   * @description get all formats for variables
   * @date 08-09-2022
   * @param {String} token
   * @returns {Object}
   * @memberof QuestionnaireService
   */

  async getAllFormatsForVariables(token) {
    let response = await axios.get(`${this.apiUrl}/format-variables`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data.data;
  }

  /**
  * @author Edgar Escobedo
  * @description get all currency units
  * @date 21-09-2022
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async getAllCurrencyUnits(token) {
    let response = await axios.get(`${this.apiUrl}/monetary-unit`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }


  /**
  * @author Edgar Escobedo
  * @description update questions
  * @date 07-10-2022
  * @param {String} questions
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async updateQuestions(questions, token) {
    let response = await axios.put(
      `${this.apiUrl}/questions`,
      { questions },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Delete questionnaire
  * @date 10-10-2022
  * @param {String} id
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async deleteQuestionnaire(id, token) {
    let response = await axios.delete(
      `${this.apiUrl}/questionnaire?id=${id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description get all info of questions for questionnaire
  * @date 14-10-2022
  * @param {String} token
  * @param {String} questionnaireId
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async getCompleteInfoQuestions(questionnaireId, token) {
    let response = await axios.get(`${this.apiUrl}/questionnaire-full?questionnaireId=${questionnaireId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description duplicate questionnaire
  * @date 14-10-2022
  * @param {String} token
  * @param {String} questionnaireId
  * @param {String} clientId
  * @param {String} questionnaireName
  * @returns {Object}
  * @memberof QuestionnaireService
  */

  async duplicateQuestionnaire(questionnaireId, clientId, questionnaireName, companyId, token) {
    let response = await axios.post(
      `${this.apiUrl}/questions-duplicate`,
      { questionnaireId, clientId, questionnaireName, companyId },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
   * @typedef {Object} SelectorOptions
   * @prop {string} SelectorOptions.name
   */

  /**
  * @author Edgar Escobedo
  * @description create a question
  * @date 01/11/2022
  * @param {String} token
  * @param {String} questionnaireId
  * @param {String} fieldTypeId
  * @param {String} question
  * @param {String} helpText
  * @param {String} questionGroup
  * @param {String} level
  * @param {String} order
  * @param {String} consecutive
  * @param {String} requireClientDocument
  * @param {String} requireTemplateVariable
  * @param {String} answerSelected
  * @param {String} answerArray
  * @param {String} templateVariableArray
  * @param {String} type
  * @param {String} formatVariableId
  * @param {String} currency
  * @param {String} amountInLetters
  * @param {String} monetaryUnit
  * @param {String} clientDocument
  * @param {SelectorOptions[]} selectorOptions 
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async createQuestion(questionnaireId, fieldTypeId, question, helpText, questionGroup, level, order, consecutive, requireClientDocument, requireTemplateVariable, templateVariableId, answerSelected, answerArray, templateVariableArray, type, formatVariableId, currency, amountInLetters, monetaryUnit, clientDocument, selectorOptions, optionsLogic, token) {
    let response = await axios.post(
      `${this.apiUrl}/create-question`,
      { questionnaireId, fieldTypeId, question, helpText, questionGroup, level, order, consecutive, requireClientDocument, requireTemplateVariable, templateVariableId, answerSelected, answerArray, templateVariableArray, type, formatVariableId, currency, amountInLetters, monetaryUnit, clientDocument, selectorOptions, optionsLogic },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description update a question
  * @date 02/11/2022
  * @param {String} token questionId, questionnaireId, fieldTypeId, question, helpText, questionGroup, level, order, consecutive, requireClientDocument, requireTemplateVariable, type, formatVariableId, currency, amountInLetters, monetaryUnit, answerArray, clientDocumentArray
  * @param {String} questionId
  * @param {String} questionnaireId
  * @param {String} fieldTypeId
  * @param {String} question
  * @param {String} helpText
  * @param {String} questionGroup
  * @param {String} level
  * @param {String} order
  * @param {String} consecutive
  * @param {String} requireClientDocument
  * @param {String} requireTemplateVariable
  * @param {String} type
  * @param {String} templateVariableArray
  * @param {String} formatVariableId
  * @param {String} currency
  * @param {String} amountInLetters
  * @param {String} monetaryUnit
  * @param {String} answerArray
  * @param {String} clientDocumentArray
  * @param {SelectorOptions} selectorOptions
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async updateQuestion(questionId, questionnaireId, fieldTypeId, question, helpText, questionGroup, level, order, consecutive, requireClientDocument, requireTemplateVariable, type, formatVariableId, currency, amountInLetters, monetaryUnit, answerArray, clientDocumentArray, selectorOptions, optionsLogic, token) {
    let response = await axios.put(
      `${this.apiUrl}/question`,
      { questionId, questionnaireId, fieldTypeId, question, helpText, questionGroup, level, order, consecutive, requireClientDocument, requireTemplateVariable, type, formatVariableId, currency, amountInLetters, monetaryUnit, answerArray, clientDocumentArray, selectorOptions, optionsLogic },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description update order questions
  * @date 04-11-2022
  * @param {String} token
  * @param {String} questionId
  * @param {String} level
  * @param {String} order
  * @param {String} consecutive
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async updateOrderQuestion(questionArray, token) {
    let response = await axios.put(
      `${this.apiUrl}/question-order-update`,
      { questionArray },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description delete question
  * @date 05-11-2022
  * @param {String} token
  * @param {String} id
  * @param {String} idArray
  * @returns {Object}
  * @memberof QuestionnaireService
  */


  async deleteQuestion(id, idArray, token) {
    let response = await axios.delete(
      `${this.apiUrl}/question?id=${id}&idArray=${idArray}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }


  /**
  * @author Edgar Escobedo
  * @description verify if exist document
  * @date 09-11-2022
  * @param {String} token
  * @param {String} clientDocumentName
  * @param {String} clientId
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async verifyIfExistDocumentWithSameName(clientDocumentName, clientId, token) {
    let response = await axios.get(`${this.apiUrl}/clientDocument-exist?clientDocumentName=${clientDocumentName}&clientId=${clientId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response;
  }


  /**
* @author Edgar Escobedo
* @description change status questionnaire to false
* @date 03-02-2023
* @param {String} id
* @param {String} description
* @param {String} status
* @param {String} token
* @returns {Object}
* @memberof QuestionnaireService
*/
  async updateQuestionnaireStatus(id, description, status, token) {
    let response = await axios.put(
      `${this.apiUrl}/update-status-questionnaire`,
      { id, status, description },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Create or update document in answerRawRecDoc 
  * @date 28-04-2023
  * @param {String} id
  * @param {String} documentQuestion
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async createDocumentAnswerRawRecDoc(documentQuestion, token) {
    let response = await axios.post(
      `${this.apiUrl}/document-answerRawRecDoc`,
      documentQuestion,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description Create or update classifier
  * @date 14-06-2024
  * @param {String} solicitudeId
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async createOrUpdateClassifier(solicitudeId, companyId, categoryId, subCategoryId, typeDocument, pathBreadcrumb, token) {
    let response = await axios.post(
      `${this.apiUrl}/classfier-categories-questionnaire`,
      { solicitudeId, companyId, categoryId, subCategoryId, typeDocument, pathBreadcrumb },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  }

  /**
  * @author Edgar Escobedo
  * @description GET classifier information by questionnaire
  * @date 05-07-2024
  * @param {String} solicitudeId
  * @param {String} token
  * @returns {Object}
  * @memberof QuestionnaireService
  */
  async getClassifierByQuestionnaireId(questionnaireId, token) {
    let response = await axios.get(`${this.apiUrl}/category-assigned-by-questionnaire?questionnaireId=${questionnaireId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }
}

export { QuestionnaireService as default };